import React, { useState, useEffect } from 'react'
import Sidebar from '../mini-comp/Sidebar'
import Topbar from '../mini-comp/Topbar'
import axios from 'axios';
import Cookies from 'js-cookie';
import API_BASE_URL from '../../../config/api';
import Swal from 'sweetalert2';
import { RiEyeLine, RiEditLine, RiDeleteBinLine, RiCalendarLine, RiSearchLine } from 'react-icons/ri';

const AdminHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: ''
  });

  const fetchHolidays = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${API_BASE_URL}/api/v1/employees/holidays/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHolidays(Array.isArray(response.data) ? response.data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      setLoading(false);
      Swal.fire('Error', 'Failed to fetch holidays.', 'error');
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setIsEditMode(true);
    setFormData({
      name: holiday.name || holiday.title || '',
      date: holiday.date || '',
      description: holiday.description || ''
    });
  };

  const handleCloseModal = () => {
    setIsEditMode(false);
    setEditingHoliday(null);
    setFormData({
      name: '',
      date: '',
      description: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const token = Cookies.get('token');

    try {
      if (isEditMode && editingHoliday) {
        await axios.patch(`${API_BASE_URL}/api/v1/employees/holidays/${editingHoliday.id}/`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        Swal.fire('Success', 'Holiday updated successfully!', 'success');
      } else {
        await axios.post(`${API_BASE_URL}/api/v1/employees/holidays/`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        Swal.fire('Success', 'Holiday created successfully!', 'success');
      }
      handleCloseModal();
      fetchHolidays();
      // Close Bootstrap Modal
      const modalElement = document.getElementById('holidayModal');
      const modalInstance = window.bootstrap?.Modal?.getInstance(modalElement);
      if (modalInstance) modalInstance.hide();
    } catch (error) {
      console.error('Error saving holiday:', error);
      Swal.fire('Error', 'Failed to save holiday.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete "${name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const token = Cookies.get('token');
        await axios.delete(`${API_BASE_URL}/api/v1/employees/holidays/${id}/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        Swal.fire('Deleted!', 'Holiday has been deleted.', 'success');
        fetchHolidays();
      } catch (error) {
        console.error('Error deleting holiday:', error);
        Swal.fire('Error', 'Failed to delete holiday.', 'error');
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const filteredHolidays = holidays.filter(holiday => {
    const name = (holiday.name || holiday.title || '').toLowerCase();
    const date = formatDate(holiday.date).toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || date.includes(search);
  });

  return (
    <div>
      <Topbar />
      <Sidebar />

      <div className="app-content-area">
        <div className="app-content-wrap">
          <div className="container-fluid">
            <div className="row mb-4 align-items-center">
              <div className="col-md-4">
                <h4 className="mb-0">Public Holidays <span className="badge bg-label-primary">{filteredHolidays.length}</span></h4>
              </div>
              <div className="col-md-5 mt-3 mt-md-0">
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                    <RiSearchLine className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search by holiday name or date..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3 mt-3 mt-md-0 text-md-end">
                <button
                  className="btn btn-primary w-100 w-md-auto"
                  data-bs-toggle="modal"
                  data-bs-target="#holidayModal"
                  onClick={() => {
                    handleCloseModal();
                    setIsEditMode(false);
                  }}
                >
                  Add Holiday
                </button>
              </div>
            </div>

            {loading ? (
              <div className="row justify-content-center mt-5">
                <div className="col-auto text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Fetching holidays...</p>
                </div>
              </div>
            ) : (
              <div className="row">
                {filteredHolidays.length > 0 ? (
                  filteredHolidays.map((holiday) => (
                    <div key={holiday.id} className="col-xxl-3 col-xl-4 col-md-6 mb-4">
                      <div className="card h-100 shadow-sm border-0 border-top border-4 border-primary">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="bg-primary-transparent text-primary p-2 rounded">
                              <RiCalendarLine size={24} />
                            </div>
                            <span className="badge bg-label-secondary">{formatDate(holiday.date)}</span>
                          </div>
                          <h5 className="card-title mb-2 text-dark">{holiday.name || holiday.title}</h5>
                          <p className="card-text text-muted mb-0">
                            {holiday.description || 'No description provided for this public holiday.'}
                          </p>
                        </div>
                        <div className="card-footer bg-transparent border-0 pt-0 pb-3">
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-icon btn-info-light"
                              data-bs-toggle="modal"
                              data-bs-target="#holidayModal"
                              onClick={() => handleEdit(holiday)}
                            >
                              <RiEditLine />
                            </button>
                            <button
                              className="btn btn-sm btn-icon btn-danger-light"
                              onClick={() => handleDelete(holiday.id, holiday.name || holiday.title)}
                            >
                              <RiDeleteBinLine />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-12 text-center py-5">
                    <div className="mb-3">
                      <RiSearchLine size={64} className="text-muted opacity-25" />
                    </div>
                    <h5 className="text-muted">No holidays found</h5>
                    <p className="text-muted">
                      {searchTerm ? `No results matching "${searchTerm}"` : "There are currenty no public holidays scheduled."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Holiday Modal */}
      <div className="modal fade" id="holidayModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title font-bold">
                  {isEditMode ? 'Edit Holiday' : 'Add New Holiday'}
                </h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Holiday Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter holiday name"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter short description..."
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-light" data-bs-dismiss="modal" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : (isEditMode ? 'Update Holiday' : 'Save Holiday')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHolidays